import { ArrayUtils } from '@shopping/common'
import { CommonState } from 'common/types/commonState'
import { StoreProducts } from 'common/types/entities/store'
import { StateKey } from 'common/utils/flipState'
import { ProductParam, RelationProductParam } from 'common/types/params'
import {
  getVerticalBySubVertical,
  ListItem,
  SubVertical,
  hasMultipleSubVerticals,
  isAlreadyDoneError,
  resolveZzimErrorMessage,
} from 'common/constants'
import { getVisibleGifProducts, WindowProduct } from 'common/types/entities/mongo/product'
import { RelationProduct } from 'common/types/entities/mongo/relationProduct'
import {
  fetchOneRelationProduct,
  fetchProductsByProductParam,
  fetchProductsWithZzim,
  fetchRelationProductsWithZzim,
} from 'common/services/list'
import { fetchRelationProductList } from 'common/services/relationProduct'
import { RelationProductList } from 'common/types/entities/list'
import { fetchSpecialEvents } from 'common/services/specialEvent'
import { EventsState } from 'common/types/entities/event'
import { setRelationProductZzim, setProductZzim } from 'common/services/zzim'
import { Error } from 'common/types/entities/error'
import HttpStatus from 'common/utils/httpStatus'
import { isMobileEnv } from 'common/utils/common'
import { ShortcutRenderOptions } from 'common/components/ListCard/Store'
import { SpecialEvent } from 'common/types/entities/api/specialEvent'
import { PopularityItem } from 'common/types/entities/mongo/popularity'
import { fetchPopularityBrands, fetchPopularityCategories, fetchPopularityTags } from 'common/services/popularity'
import { DisplayMenu } from 'common/types/entities/mongo/menu'

type ItemType = WindowProduct | RelationProduct | SpecialEvent | ShortcutRenderOptions | any

export interface ItemState {
  type: ListItem
  data: ItemType
  id: string
  groupKey: number
}

export interface ListItemsState {
  items: ItemState[]
  hasMore: ItemHasMore
  param?: ProductListParam
  page: number
  layoutStatus?: any
  appendStatus?: any
}

const initialState: ListItemsState = {
  items: [],
  hasMore: {
    base: true,
    product: true,
    relationProduct: true,
    specialEvents: true,
  },
  param: null,
  page: 1,
  layoutStatus: null,
  appendStatus: null,
}

export type ProductListParam = {
  productParam: ProductParam
  relationProductParam: RelationProductParam
  defaultMenu?: DisplayMenu
}

interface ItemHasMore {
  base: boolean
  product?: boolean
  relationProduct?: boolean
  specialEvents?: boolean
}

interface ExtraItem {
  index: number
  item: ItemState
  added?: boolean
}

const FETCH = 'store/ListItems/FETCH_LIST'
const SET_ZZIM = 'store/ListItems/SET_ZZIM'
const SAVE_STATUS = 'store/ListItems/SAVE_STATUS'
const APPEND_STATUS = 'store/ListItems/APPEND_STATUS'
const CHANGE_STATUS = 'store/ListItems/CHANGE_STATUS'

export const reloadList = (curState: StateKey, subVertical: SubVertical, params: ProductListParam) => {
  return fetchList(curState, false, subVertical, 1, params)
}

export const fetchList = (
  curState: StateKey,
  isFirstFetch: boolean,
  subVertical: SubVertical,
  page: number,
  params?: ProductListParam,
) => async (dispatch, getState: () => CommonState) => {
  const prevListItems = { ...getState().listItems[curState] }
  const prevListParam = prevListItems.param
  const productParam = (params && params.productParam) || (prevListParam && prevListParam.productParam)
  const relationProductParam =
    (params && params.relationProductParam) || (prevListParam && prevListParam.relationProductParam)
  const defaultMenu = (params && params.defaultMenu) || (prevListParam && prevListParam.defaultMenu)

  const isLastPage = productParam.maxPage && page >= productParam.maxPage
  const isNewList = page === 1 || isFirstFetch || ArrayUtils.isEmpty(prevListItems.items)
  const hasMore = isNewList ? { ...initialState.hasMore } : { ...prevListItems.hasMore }

  productParam.page = page
  relationProductParam.page = page

  const baseListItems: ListItemsState = await fetchBaseListItems(
    page,
    subVertical,
    hasMore,
    productParam,
    relationProductParam,
  )

  let mergedItems = []
  if (ArrayUtils.isNotEmpty(baseListItems.items)) {
    const [extraItems, extraHasMore] = await fetchExtraItems(
      page,
      subVertical,
      hasMore,
      productParam,
      relationProductParam,
      defaultMenu,
    )

    const filteredBaseItems =
      page === 1 ? baseListItems.items : filterDuplicatedBaseItems(baseListItems.items, prevListItems.items)

    mergedItems = filteredBaseItems.reduce((list, item, index) => {
      list.push(item)
      extraItems
        .filter(extraItem => extraItem.index === index + 1)
        .forEach(extraItems => {
          list.push(extraItems.item)
          extraItems.added = true
        })
      return list
    }, [])

    const remainingExtraItems = extraItems
      .filter(extraItem => extraItem.added !== true)
      .map(extraItem => extraItem.item)
    mergedItems.push(...remainingExtraItems)

    baseListItems.hasMore = { ...extraHasMore, ...baseListItems.hasMore }
  }

  const state = {
    page,
    param: {
      productParam,
      relationProductParam,
      defaultMenu,
    },
    hasMore: {
      ...prevListItems.hasMore,
      ...baseListItems.hasMore,
      base: isLastPage ? false : baseListItems.hasMore.base,
    },
    items: isNewList ? mergedItems : [...prevListItems.items, ...mergedItems],
  }

  dispatch({
    curState,
    state,
    type: FETCH,
  })
}

const fetchBaseListItems = async (
  currentPage: number,
  subVertical: SubVertical,
  itemHasMore: ItemHasMore,
  productParam: ProductParam,
  relationProductParam: RelationProductParam,
): Promise<ListItemsState> => {
  return subVertical === SubVertical.DEPARTMENT
    ? await fetchDepartmentProductList(currentPage, itemHasMore, relationProductParam, productParam)
    : await fetchProductList(currentPage, itemHasMore, productParam, subVertical)
}

const fetchProductList = async (
  currentPage: number,
  itemHasMore: ItemHasMore,
  productParam: ProductParam,
  subVertical: SubVertical,
): Promise<ListItemsState> => {
  const hasMore = { ...itemHasMore }
  const items: ItemState[] = []

  if (hasMore.product) {
    const storeProducts: StoreProducts = await fetchProductsByProductParam(productParam)
    let products = (storeProducts && storeProducts.products) || []
    if (ArrayUtils.isNotEmpty(products)) {
      products = getVisibleGifProducts(products, subVertical)
      products = await fetchProductsWithZzim(products)
      products.forEach(product =>
        items.push({
          type: ListItem.WINDOWPRODUCT,
          data: product,
          id: product._id,
          groupKey: currentPage,
        }),
      )
    }
    hasMore.product = storeProducts && storeProducts.hasMoreProducts
  }

  return {
    items,
    hasMore: {
      ...hasMore,
      base: hasMore.product,
      product: hasMore.product,
    },
    page: productParam.page,
  }
}

const fetchDepartmentProductList = async (
  currentPage: number,
  itemHasMore: ItemHasMore,
  relationProductParam: RelationProductParam,
  productParam: ProductParam,
): Promise<ListItemsState> => {
  const items: ItemState[] = []
  const hasMore = { ...itemHasMore }

  await fetchFirst(currentPage, relationProductParam, items)

  let relationProducts = []

  if (itemHasMore && itemHasMore.relationProduct) {
    const relationProductList: RelationProductList = await fetchRelationProductList(relationProductParam)
    relationProducts = relationProductList && relationProductList.relationProducts
    hasMore.relationProduct = relationProductList.hasMore || false
    if (ArrayUtils.isNotEmpty(relationProducts)) {
      relationProducts = await fetchRelationProductsWithZzim(relationProducts)
      relationProducts.forEach(relationProduct =>
        items.push({
          type: ListItem.RELATION_PRODUCT,
          data: relationProduct,
          id: relationProduct._id,
          groupKey: currentPage,
        }),
      )
    }
  }

  const productItemSize = relationProductParam.pageSize - relationProducts.length
  if (hasMore.relationProduct === false && relationProducts.length < relationProductParam.pageSize) {
    const storeProducts: StoreProducts = await fetchProductsByProductParam(productParam)
    let products = (storeProducts && storeProducts.products) || []
    hasMore.product = storeProducts.hasMoreProducts || false
    if (ArrayUtils.isNotEmpty(products)) {
      products = await fetchProductsWithZzim(products)
      products.slice(0, productItemSize).forEach(product =>
        items.push({
          type: ListItem.WINDOWPRODUCT,
          data: product,
          id: product._id,
          groupKey: currentPage,
        }),
      )
    }
  }

  return {
    items,
    hasMore: {
      ...hasMore,
      base: hasMore.relationProduct || hasMore.product,
    },
    page: relationProductParam.page,
  }
}

const fetchFirst = async (currentPage, relationProductParam, items) => {
  const firstRelationProduct = await fetchOneRelationProduct(currentPage, relationProductParam)
  firstRelationProduct &&
    items.push({
      type: ListItem.RELATION_PRODUCT,
      data: firstRelationProduct,
      id: firstRelationProduct._id,
      groupKey: currentPage,
    })
}

const fetchExtraItems = async (
  currentPage: number,
  subVertical: SubVertical,
  itemHasMore: ItemHasMore,
  productParam: ProductParam,
  relationProductParam: RelationProductParam,
  defaultMenu?: DisplayMenu,
): Promise<[ExtraItem[], ItemHasMore]> => {
  const fetchPromises = []
  const hasMore = { ...itemHasMore }

  if (relationProductParam && subVertical === SubVertical.STYLE && hasMore.relationProduct) {
    fetchPromises.push(fetchRelationProductsExtraItems(currentPage, itemHasMore, relationProductParam))
  }

  if (hasMore.specialEvents) {
    fetchPromises.push(fetchSpecialEventsExtraItem(currentPage, subVertical, hasMore))
  }

  // No popular card, if single store view
  if (!productParam.storeId) {
    if (currentPage === 1 && [SubVertical.DEPARTMENT, SubVertical.OUTLET, SubVertical.STYLE].includes(subVertical)) {
      fetchPromises.push(fetchPopularCategoryExtraItem(currentPage, subVertical, productParam))
    }

    if (
      currentPage === 2 &&
      [SubVertical.DEPARTMENT, SubVertical.OUTLET].includes(subVertical) &&
      !productParam.brandId
    ) {
      fetchPromises.push(fetchPopularBrandExtraItem(currentPage, subVertical, productParam))
    }

    if (currentPage === 2 && [SubVertical.STYLE].includes(subVertical)) {
      fetchPromises.push(fetchPopularTagExtraItem(currentPage, subVertical, productParam))
    }
  }

  const fetchedItems = await Promise.all(fetchPromises)
  const extraItems = fetchedItems
    .filter(item => !!item)
    .reduce((items, item) => {
      return [...items, ...item]
    }, [])

  // No coordi link card, if single store view
  if (!productParam.storeId) {
    if (currentPage === 1 && [SubVertical.STYLE].includes(subVertical) && defaultMenu) {
      const item = {
        type: ListItem.LINK,
        data: defaultMenu,
        id: defaultMenu.id,
        groupKey: currentPage,
      }
      extraItems.push({ item, index: 5 })
    }
  }

  if (currentPage === 2 && isMobileEnv && navigator.userAgent.includes('Android')) {
    const item = {
      type: ListItem.SHORTCUT,
      data: { isHomeShorcut: true },
      id: null,
      groupKey: currentPage,
    }
    extraItems.push({ item, index: 10 })
  }
  return [extraItems, hasMore]
}

const fetchRelationProductsExtraItems = async (
  currentPage: number,
  hasMore: ItemHasMore,
  relationProductParam?: RelationProductParam,
): Promise<ExtraItem[]> => {
  relationProductParam.pageSize = 2
  const relationProductList: RelationProductList = await fetchRelationProductList(relationProductParam)
  hasMore.relationProduct = relationProductList && relationProductList.hasMore
  if (relationProductList && ArrayUtils.isNotEmpty(relationProductList.relationProducts)) {
    const relationProducts = await fetchRelationProductsWithZzim(relationProductList.relationProducts)
    return relationProducts.map((relationProduct, index) => {
      const item = {
        type: ListItem.RELATION_PRODUCT,
        data: relationProduct,
        id: relationProduct._id,
        groupKey: currentPage,
      }
      const relationProductIndex = index === 0 ? 5 : 10
      return { item, index: relationProductIndex }
    })
  }
}

const fetchSpecialEventsExtraItem = async (
  currentPage: number,
  subVertical: SubVertical,
  hasMore: ItemHasMore,
): Promise<ExtraItem> => {
  const vertical = getVerticalBySubVertical(subVertical)
  const param = {
    vertical,
    page: currentPage,
    pageSize: 1,
    subVertical: hasMultipleSubVerticals(vertical) ? subVertical : null,
  }
  const specialEvents: EventsState = await fetchSpecialEvents(param)
  hasMore.specialEvents = specialEvents && specialEvents.hasMore
  if (specialEvents && ArrayUtils.isNotEmpty(specialEvents.events)) {
    const item = {
      type: ListItem.SPECIAL_EVENT,
      data: specialEvents.events[0],
      id: specialEvents.events[0].id,
      groupKey: currentPage,
    }
    return { item, index: 8 }
  }
}

const fetchPopularCategoryExtraItem = async (
  currentPage: number,
  subVertical: SubVertical,
  productParam?: ProductParam,
): Promise<ExtraItem> => {
  const popularCategories: PopularityItem[] = await fetchPopularityCategories(subVertical, productParam.menuId)
  const popularData = {
    items: popularCategories,
    menuId: productParam.menuId,
    brandId: productParam.brandId,
    isZzims: (productParam.brandIds && productParam.brandIds.length > 0) || false,
  }
  if (ArrayUtils.isNotEmpty(popularCategories)) {
    const item = {
      type: ListItem.POPULARITY,
      data: popularData,
      id: 'popularCategories',
      groupKey: currentPage,
    }
    return { item, index: 7 }
  }
}

const fetchPopularBrandExtraItem = async (
  currentPage: number,
  subVertical: SubVertical,
  productParam?: ProductParam,
): Promise<ExtraItem> => {
  const popularBrands: PopularityItem[] = await fetchPopularityBrands(subVertical, productParam.menuId)
  const popularData = { items: popularBrands }
  if (ArrayUtils.isNotEmpty(popularBrands)) {
    const item = {
      type: ListItem.POPULARITY,
      data: popularData,
      id: 'popularBrands',
      groupKey: currentPage,
    }
    return { item, index: 4 }
  }
}

const fetchPopularTagExtraItem = async (
  currentPage: number,
  subVertical: SubVertical,
  productParam?: ProductParam,
): Promise<ExtraItem> => {
  const recommendedTags: PopularityItem[] = await fetchPopularityTags(subVertical, productParam.menuId)
  const popularData = { items: recommendedTags }
  if (ArrayUtils.isNotEmpty(recommendedTags)) {
    const item = {
      type: ListItem.POPULARITY,
      data: popularData,
      id: 'recommendedTags',
      groupKey: currentPage,
    }
    return { item, index: 4 }
  }
}

export const saveAppendStatus = (curState: StateKey, path: string, infiniteStatus: any) => async (
  dispatch,
  getState,
) => {
  const prevListItems = getState().listItems[curState]

  const state = {
    ...prevListItems,
    appendStatus: infiniteStatus,
  }

  dispatch({
    curState,
    state,
    type: APPEND_STATUS,
  })
}

export const changeAppendStatus = (curState: StateKey) => async (dispatch, getState) => {
  const prevListItems = getState().listItems[curState]

  if (prevListItems.appendStatus) {
    const state = {
      ...prevListItems,
      layoutStatus: prevListItems.appendStatus = {
        ...prevListItems.appendStatus,
        _watcher: {
          _prevPos: window.pageYOffset - 670,
          scrollPos: window.pageYOffset,
        },
      },
      appendStatus: null,
    }

    dispatch({
      curState,
      state,
      type: CHANGE_STATUS,
    })
  }
}

export const saveRestorationStatus = (curState: StateKey, path: string, infiniteStatus: any) => async (
  dispatch,
  getState,
) => {
  const prevListItems = getState().listItems[curState]

  const state = {
    ...prevListItems,
    layoutStatus: infiniteStatus,
  }

  dispatch({
    curState,
    state,
    type: SAVE_STATUS,
  })
}

export const removeRestorationStatus = (curState: StateKey) => async (dispatch, getState) => {
  const prevListItems = getState().listItems[curState]

  const state = {
    ...prevListItems,
    layoutStatus: null,
  }

  dispatch({
    curState,
    state,
    type: SAVE_STATUS,
  })
}

export const toggleZzim = (
  curState: StateKey,
  productId: string,
  zzimed: boolean,
  listItem: ListItem,
) => async dispatch => {
  let responseData
  if (listItem === ListItem.RELATION_PRODUCT) {
    responseData = await setRelationProductZzim(productId, zzimed)
  } else {
    responseData = await setProductZzim(productId, zzimed)
  }
  if (!responseData.bSuccess) {
    const error: Error = {
      status: responseData.sErrorCode === 'ACCESS_DENIED' ? HttpStatus.UNAUTHORIZED : HttpStatus.OK,
      message: resolveZzimErrorMessage(
        responseData.htReturnValue.sErrorCode,
        zzimed,
        responseData.htReturnValue.sMessage,
      ),
    }
    if (isAlreadyDoneError(responseData.htReturnValue.sErrorCode)) {
      dispatch({
        curState,
        productId,
        listItem,
        type: SET_ZZIM,
      })
    }
    throw error
  }

  dispatch({
    curState,
    productId,
    listItem,
    type: SET_ZZIM,
  })
}

const findProductItemIndex = (items: ItemState[], productId: string, listItem: ListItem) => {
  return items && items.findIndex(item => item.type === listItem && item.data._id === productId)
}

const getZzimToggledProductItem = (items: ItemState) => {
  return {
    ...items,
    data: {
      ...items.data,
      isMyZzim: !items.data.isMyZzim,
    },
  }
}

const filterDuplicatedBaseItems = (target: ItemState[] = [], source: ItemState[] = []) => {
  const baseCardIds = source.reduce((ids, sourceItem) => {
    if ([ListItem.WINDOWPRODUCT, ListItem.RELATION_PRODUCT].includes(sourceItem.type)) {
      ids.add(sourceItem.id)
    }
    return ids
  }, new Set())
  return target.filter(targetItem => !baseCardIds.has(targetItem.id))
}

const relatedProductReducer = (state = initialState, action) => {
  switch (action.type) {
    case FETCH:
    case SAVE_STATUS:
      return action.state
    case APPEND_STATUS:
      return action.state
    case CHANGE_STATUS:
      return action.state
    case SET_ZZIM:
      const targetItemIndex = findProductItemIndex(state.items, action.productId, action.listItem)
      return {
        ...state,
        items: [
          ...state.items.slice(0, targetItemIndex),
          getZzimToggledProductItem(state.items[targetItemIndex]),
          ...state.items.slice(targetItemIndex + 1),
        ],
      }
    default:
      return state
  }
}

export default relatedProductReducer
